import type { GroupAllocation } from '@orderlymeds/measurements-schemas';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateExclusionGroup } from '../../api/exclusion-groups';
import { useExperiments } from '../../api/experiments';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { getSegmentColor, SegmentedBar } from '../../components/SegmentedBar';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import shared from '../shared.module.css';
import styles from './ExclusionGroupCreatePage.module.css';

interface AllocationRow {
	key: string;
	experiment_id: string;
	range_start: number;
	range_end: number;
}

let rowKeyCounter = 0;
function nextRowKey() {
	rowKeyCounter += 1;
	return `row${rowKeyCounter}`;
}

function validateAllocations(rows: AllocationRow[]): string | null {
	if (rows.length < 2) return 'At least 2 allocations required.';

	const sorted = [...rows].sort((a, b) => a.range_start - b.range_start);

	if (sorted[0]?.range_start !== 0) return 'First allocation must start at 0.';

	for (let i = 1; i < sorted.length; i++) {
		const prev = sorted[i - 1];
		const curr = sorted[i];
		if (!prev || !curr) continue;
		if (curr.range_start !== prev.range_end) {
			return `Allocation ranges must be contiguous (gap between ${prev.range_end} and ${curr.range_start}).`;
		}
	}

	const last = sorted[sorted.length - 1];
	if (!last || last.range_end !== 10000) return 'Last allocation must end at 10000.';

	for (const row of rows) {
		if (!row.experiment_id) return 'All allocations must have an experiment selected.';
		if (row.range_start >= row.range_end)
			return 'Each allocation must have range_start < range_end.';
	}

	return null;
}

export function ExclusionGroupCreatePage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const { data: experiments } = useExperiments(id ?? '');
	const createGroup = useCreateExclusionGroup(id ?? '');
	const breadcrumbs = useProjectBreadcrumbs('Exclusion Groups', 'New');

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [rows, setRows] = useState<AllocationRow[]>([
		{ key: nextRowKey(), experiment_id: '', range_start: 0, range_end: 5000 },
		{ key: nextRowKey(), experiment_id: '', range_start: 5000, range_end: 10000 },
	]);

	const experimentOptions = (experiments ?? []).map((exp) => ({
		value: exp.id,
		label: exp.name,
	}));

	const validationError = validateAllocations(rows);
	const isValid = !validationError && name.trim().length > 0;

	function handleAddRow() {
		setRows((prev) => [
			...prev,
			{ key: nextRowKey(), experiment_id: '', range_start: 0, range_end: 0 },
		]);
	}

	function handleRemoveRow(key: string) {
		if (rows.length <= 2) return;
		setRows((prev) => prev.filter((r) => r.key !== key));
	}

	function handleRowChange(key: string, field: keyof AllocationRow, value: string | number) {
		setRows((prev) =>
			prev.map((r) => {
				if (r.key !== key) return r;
				return { ...r, [field]: value };
			}),
		);
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!isValid) return;

		const allocations: GroupAllocation[] = rows.map((r) => ({
			experiment_id: r.experiment_id,
			range_start: r.range_start,
			range_end: r.range_end,
		}));

		const result = await createGroup.mutateAsync({
			name: name.trim(),
			description: description.trim() || undefined,
			allocations,
		});

		navigate(`/projects/${id}/exclusion-groups/${result.id}`);
	}

	// Build allocation bar segments from rows
	const totalRange = 10000;
	const barRows = [...rows].sort((a, b) => a.range_start - b.range_start);

	return (
		<div>
			<PageHeader title="New Exclusion Group" breadcrumbs={breadcrumbs} />

			<div className={shared.content}>
				<Card>
					<form onSubmit={handleSubmit} className={shared.form}>
						<Input
							label="Name"
							name="group-name"
							value={name}
							onChange={setName}
							required
							placeholder="Homepage Hero Test Group"
						/>
						<Input
							label="Description"
							name="group-description"
							value={description}
							onChange={setDescription}
							placeholder="Optional description"
						/>

						<div className={styles.section}>
							<h3 className={shared.sectionTitle}>Allocations</h3>
							<p className={styles.sectionHint}>
								Assign contiguous ranges (0–10000) to experiments. Each visitor's group bucket
								determines which experiment they're eligible for.
							</p>

							{/* Allocation bar */}
							<SegmentedBar
								segments={barRows.map((row, i) => {
									const expName =
										experiments?.find((e) => e.id === row.experiment_id)?.name ||
										row.experiment_id ||
										`Slot ${i + 1}`;
									return {
										key: row.key,
										label: expName,
										value: row.range_end - row.range_start,
										title: `${expName}: ${(((row.range_end - row.range_start) / totalRange) * 100).toFixed(1)}%`,
									};
								})}
								denominator={totalRange}
							/>

							{/* Allocation table header */}
							<div className={styles.allocHeader}>
								<span className={styles.allocColHeader} />
								<span className={styles.allocColHeader}>Experiment</span>
								<span className={styles.allocColHeader}>Range Start</span>
								<span className={styles.allocColHeader}>Range End</span>
								<span className={styles.allocColHeader}>%</span>
								<span />
							</div>

							{/* Allocation rows */}
							{rows.map((row, i) => {
								const color = getSegmentColor(i);
								const pct = (((row.range_end - row.range_start) / totalRange) * 100).toFixed(1);
								return (
									<div key={row.key} className={styles.allocRow}>
										<div className={styles.colorDot} style={{ backgroundColor: color }} />
										<select
											className={styles.allocSelect}
											value={row.experiment_id}
											onChange={(e) => handleRowChange(row.key, 'experiment_id', e.target.value)}
											required
										>
											<option value="">Select experiment…</option>
											{experimentOptions.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{opt.label}
												</option>
											))}
										</select>
										<input
											className={[styles.allocInput, shared.mono].join(' ')}
											type="number"
											value={row.range_start}
											onChange={(e) =>
												handleRowChange(row.key, 'range_start', parseInt(e.target.value, 10) || 0)
											}
											min={0}
											max={10000}
											required
										/>
										<input
											className={[styles.allocInput, shared.mono].join(' ')}
											type="number"
											value={row.range_end}
											onChange={(e) =>
												handleRowChange(row.key, 'range_end', parseInt(e.target.value, 10) || 0)
											}
											min={0}
											max={10000}
											required
										/>
										<span className={[styles.allocPct, shared.mono].join(' ')}>{pct}%</span>
										<button
											type="button"
											className={styles.removeBtn}
											onClick={() => handleRemoveRow(row.key)}
											disabled={rows.length <= 2}
											aria-label="Remove allocation"
										>
											✕
										</button>
									</div>
								);
							})}

							<div className={styles.allocFooter}>
								<button type="button" className={styles.addBtn} onClick={handleAddRow}>
									+ Add allocation
								</button>
								{validationError && (
									<span className={styles.validationError}>{validationError}</span>
								)}
							</div>
						</div>

						{createGroup.error && (
							<p className={shared.formError}>
								{createGroup.error instanceof Error
									? createGroup.error.message
									: 'Failed to create exclusion group'}
							</p>
						)}

						<div className={shared.formActions}>
							<Button
								variant="secondary"
								type="button"
								onClick={() => navigate(`/projects/${id}/exclusion-groups`)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								type="submit"
								loading={createGroup.isPending}
								disabled={!isValid}
							>
								Create Group
							</Button>
						</div>
					</form>
				</Card>
			</div>
		</div>
	);
}
